import { WebAthlete } from '../types/athlete';
import { AthleteFormValues } from '@/components/athletes/context/AthleteFormContextCreate';

/**
 * Common Helper Utilities
 */

/**
 * Converts form data to the WebAthlete format for API/localStorage
 */
export const convertFormToAthlete = (formData: AthleteFormValues, existingAthlete?: WebAthlete): WebAthlete => {
  // Start with existing athlete data or empty object
  const athlete: Partial<WebAthlete> = {
    // Required fields must be present
    id: existingAthlete?.id || `athlete-${Date.now()}`,
    first_name: formData.firstName,
    last_name: formData.lastName,
    date_of_birth: formData.dateOfBirth,
    created_at: existingAthlete?.created_at || new Date().toISOString(),
    updated_at: new Date().toISOString(),
    
    // Optional fields
    gender: formData.gender as 'male' | 'female' | 'other',
    status: formData.athleteStatus as 'active' | 'inactive' | 'pending',
    email: formData.email || undefined,
    phone: formData.phone || undefined,
    profile_image: existingAthlete?.profile_image || undefined,
    avatar_url: formData.profilePhoto || undefined,
    
    // Address info
    address: {
      street: formData.street || undefined,
      city: formData.city || undefined,
      state: formData.state || undefined,
      zip: formData.zipCode || undefined
    },
    
    // Uniform sizing
    uniform_size: {
      top: formData.topSize || undefined,
      bottom: formData.bottomSize || undefined
    },
    
    // Convert emergency contacts from form array to database structure
    emergency_contacts: formData.emergencyContacts && formData.emergencyContacts.length > 0 
      ? formData.emergencyContacts.map(contact => ({
          name: contact.name || undefined,
          phone: contact.phone || undefined,
          relationship: contact.relationship || undefined,
          priority: contact.priority
        }))
      : [],
    
    // USASF membership
    usasf_membership: {
      membership_number: formData.usasfId || undefined,
      status: formData.usasfStatus as any || undefined,
      expiration_date: formData.usasfExpirationDate || undefined
    },
    
    // Skill level and experience
    skill_level: {
      tumbling_level: formData.skillLevel || undefined,
      stunting_positions: formData.stuntingPositions || undefined,
      prior_experience: formData.priorExperience || undefined,
      special_skills: formData.specialSkills || undefined
    },
    
    // Also set top-level fields for backwards compatibility
    stunting_positions: formData.stuntingPositions || undefined,
    prior_experience: formData.priorExperience || undefined,
    special_skills: formData.specialSkills || undefined,
    
    // Medical information
    medical_info: {
      allergies: formData.allergies || undefined,
      medical_conditions: formData.medicalConditions || undefined,
      medications: formData.medications || undefined,
      insurance_provider: formData.insuranceProvider || undefined,
      policy_number: formData.insurancePolicyNumber || undefined,
      physician_name: formData.physicianName || undefined,
      physician_phone: formData.physicianPhone || undefined
    }
  };
  
  // Handle authorized pickup contacts - ensure it's always an array
  if (formData.authorizedPickups && formData.authorizedPickups.length > 0) {
    athlete.authorized_pickup = formData.authorizedPickups.map(pickup => ({
      name: pickup.name,
      phone: pickup.phone || undefined,
      relationship: pickup.relationship || undefined
    }));
  } else {
    athlete.authorized_pickup = [];
  }
  
  return athlete as WebAthlete;
};

/**
 * Converts WebAthlete data to the form data format
 */
export const convertAthleteToForm = (athlete: WebAthlete): Partial<AthleteFormValues> => {
  return {
    firstName: athlete.first_name || '',
    lastName: athlete.last_name || '',
    dateOfBirth: athlete.date_of_birth || '',
    gender: athlete.gender || '',
    email: athlete.email || '',
    phone: athlete.phone || '',
    profilePhoto: athlete.avatar_url || '',
    athleteStatus: athlete.status || 'pending',
    
    // Address
    street: athlete.address?.street || '',
    city: athlete.address?.city || '',
    state: athlete.address?.state || '',
    zipCode: athlete.address?.zip || '',
    
    // Uniform sizing
    topSize: athlete.uniform_size?.top || '',
    bottomSize: athlete.uniform_size?.bottom || '',
    
    // Emergency contacts
    emergencyContacts: Array.isArray(athlete.emergency_contacts) && athlete.emergency_contacts.length > 0
      ? athlete.emergency_contacts.map((contact, index) => ({
          id: `emergency-${index}`,
          name: contact.name || '',
          phone: contact.phone || '',
          relationship: contact.relationship || '',
          priority: contact.priority || index + 1
        }))
      : [],
    
    // USASF membership
    usasfId: athlete.usasf_membership?.membership_number || '',
    usasfStatus: athlete.usasf_membership?.status || 'pending',
    usasfExpirationDate: athlete.usasf_membership?.expiration_date || '',
    
    // Skill level and experience
    skillLevel: typeof athlete.skill_level?.tumbling_level === 'number' ? athlete.skill_level.tumbling_level : 0,
    stuntingPositions: Array.isArray(athlete.skill_level?.stunting_positions) ? athlete.skill_level.stunting_positions : [],
    priorExperience: athlete.skill_level?.prior_experience || '',
    specialSkills: athlete.skill_level?.special_skills || '',
    
    // Medical information
    allergies: athlete.medical_info?.allergies || '',
    medicalConditions: athlete.medical_info?.medical_conditions || '',
    medications: athlete.medical_info?.medications || '',
    insuranceProvider: athlete.medical_info?.insurance_provider || '',
    insurancePolicyNumber: athlete.medical_info?.policy_number || '',
    physicianName: athlete.medical_info?.physician_name || '',
    physicianPhone: athlete.medical_info?.physician_phone || '',
    
    // Authorized pickup
    authorizedPickups: Array.isArray(athlete.authorized_pickup) && athlete.authorized_pickup.length > 0
      ? athlete.authorized_pickup.map((pickup, index) => ({
          id: `pickup-${index}`,
          name: pickup.name,
          phone: pickup.phone || '',
          relationship: pickup.relationship || ''
        }))
      : []
  };
};

/**
 * Utility function to conditionally join class names together
 * This is often used for combining Tailwind CSS classes
 */
export function cn(...classes: (string | undefined | null | false)[]) {
  return classes.filter(Boolean).join(' ');
}

/**
 * Capitalizes the first letter of a string
 * @param str String to capitalize
 * @returns Capitalized string
 */
export const capitalize = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}; 